/**
 * MonitoringDashboard
 *
 * A viewport-filling, no-scroll monitoring interface with three distinct
 * full-height pages navigated by a left nav rail:
 *
 *   Overview  — live state + current-interval headroom (right now)
 *   Flexibility Band  — 48h flexibility band chart (near future)
 *   History   — session patterns: heatmap + SoC scatter (past behaviour)
 *
 * The outermost container fills exactly the available height below the
 * pool page chrome (pool header + tab list) so no page-level scrolling occurs.
 * Each view is a fixed CSS grid that fills 100% of that container.
 */

import { useState, useMemo, useEffect, useRef } from 'react'
import { useMonitoringParams } from '@/hooks/use-monitoring-params'
import {
  Activity,
  TrendingUp,
  CalendarDays,
  RefreshCw,
  Loader2,
  ArrowUp,
  ArrowDown,
  Zap,
  Car,
  ChevronRight,
  ChevronLeft,
  PlugZap,
  CheckCircle2,
  BatteryCharging,
  Battery,
  Star,
  Scale,
  AlertTriangle,
  XCircle,
  Gauge,
  LogOut,
  CircleDollarSign,
  TrendingDown,
  BarChart2,
  Sun,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { InfoTip } from '@/components/ui/info-tip'
import { cn, formatEnergy, formatPower, energyToIntervalPower } from '@/lib/utils'
import {
  PeriodGridPicker,
  MONTH_NAMES,
  getISOWeek,
  isoWeekToMonday,
} from '@/components/ui/PeriodGridPicker'
import {
  usePoolState,
  useFlexBands,
  useChargingSessions,
  usePoolGCPForecasts,
  type TimeRange,
} from '@/hooks/use-monitoring'
import FlexibilityBandChart from './FlexibilityBandChart'
import SoCJourneyScatter from './SoCJourneyScatter'
import SessionActivityHeatmap from './SessionActivityHeatmap'
import D3FlexChart, { type D3FlexChartPoint, type D3FlexChartSeries } from './D3FlexChart'
import type { components } from '../../../../../types/flex-api'

type BatteryFlexibilities = components['schemas']['BatteryFlexibilities']
type EVCurrentState = components['schemas']['EVCurrentState']
type MeterFlexibilities = components['schemas']['MeterFlexibilities']
type PVFlexibilities = components['schemas']['PVFlexibilities']

// ─── Types ────────────────────────────────────────────────────────────────────

type View = 'overview' | 'dispatch' | 'forecast' | 'history' | 'gcp_forecasts'
type DisplayMode = 'power' | 'energy'

interface MonitoringDashboardProps {
  poolID: string
  poolName: string
  poolPurpose?: string
  forecastConfigs?: Array<{ purpose: string; isActive: boolean }>
}

// ─── Countdown hook ───────────────────────────────────────────────────────────

function useCountdown(targetMs: number | null): string {
  const [now, setNow] = useState(Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])
  if (targetMs === null) return ''
  const remaining = Math.max(0, targetMs - now)
  const mins = Math.floor(remaining / 60000)
  const secs = Math.floor((remaining % 60000) / 1000)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

// ─── Nav rail ─────────────────────────────────────────────────────────────────

interface NavItemProps {
  icon: React.ReactNode
  label: string
  sublabel: string
  active: boolean
  disabled?: boolean
  onClick: () => void
}

function NavItem({ icon, label, sublabel, active, disabled, onClick }: NavItemProps) {
  return (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={cn(
        'w-full flex items-center gap-3 rounded-lg px-3 py-3 text-left transition-all',
        disabled
          ? 'text-muted-foreground/40 cursor-not-allowed'
          : active
            ? 'bg-primary text-primary-foreground shadow-sm'
            : 'text-muted-foreground hover:bg-muted hover:text-foreground'
      )}
    >
      <div
        className={cn(
          'shrink-0',
          disabled
            ? 'text-muted-foreground/40'
            : active
              ? 'text-primary-foreground'
              : 'text-muted-foreground'
        )}
      >
        {icon}
      </div>
      <div className="min-w-0 flex-1 flex items-center gap-1.5">
        <span
          className={cn(
            'text-sm font-medium',
            disabled
              ? 'text-muted-foreground/40'
              : active
                ? 'text-primary-foreground'
                : 'text-foreground'
          )}
        >
          {label}
        </span>
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
        <span
          onClick={(e) => e.stopPropagation()}
          className={cn(active ? 'text-primary-foreground/70' : '')}
        >
          <InfoTip content={sublabel} side="right" />
        </span>
      </div>
      {active && !disabled && <ChevronRight className="h-3.5 w-3.5 shrink-0 text-primary-foreground/70" />}
    </button>
  )
}

// ─── Stat tile ────────────────────────────────────────────────────────────────

interface StatTileProps {
  icon: React.ReactNode
  label: string
  value: string
  sub?: string
  accent?: 'emerald' | 'rose' | 'amber' | 'blue' | 'sky' | 'violet' | 'teal' | 'orange' | 'default'
  loading?: boolean
  info?: React.ReactNode
  onClick?: () => void
}

function StatTile({
  icon,
  label,
  value,
  sub,
  accent = 'default',
  loading,
  info,
  onClick,
}: StatTileProps) {
  const accentMap: Record<
    NonNullable<StatTileProps['accent']>,
    { border: string; icon: string }
  > = {
    emerald: { border: 'border-l-emerald-500', icon: 'text-emerald-500' },
    rose: { border: 'border-l-rose-500', icon: 'text-rose-500' },
    amber: { border: 'border-l-amber-500', icon: 'text-amber-500' },
    blue: { border: 'border-l-blue-500', icon: 'text-blue-500' },
    sky: { border: 'border-l-sky-400', icon: 'text-sky-400' },
    violet: { border: 'border-l-violet-500', icon: 'text-violet-500' },
    teal: { border: 'border-l-teal-500', icon: 'text-teal-500' },
    orange: { border: 'border-l-orange-500', icon: 'text-orange-500' },
    default: { border: 'border-l-border', icon: 'text-muted-foreground' },
  }

  const { border, icon: iconColor } = accentMap[accent]

  return (
    <div
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') onClick()
            }
          : undefined
      }
      className={cn(
        'rounded-lg border-l-4 bg-card border border-border px-5 py-4 flex items-center gap-4',
        border,
        onClick &&
          'cursor-pointer hover:border-border/80 hover:bg-accent/30 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
      )}
    >
      <div className={cn('shrink-0', iconColor)}>{icon}</div>
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-1 text-xs text-muted-foreground truncate">
          {label}
          {info && <InfoTip content={info} />}
        </p>
        {loading ? (
          <div className="h-7 w-24 bg-muted animate-pulse rounded mt-1" />
        ) : (
          <p className="text-2xl font-bold tracking-tight leading-tight mt-0.5">{value}</p>
        )}
        {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
      </div>
    </div>
  )
}

// ─── Headroom bar ─────────────────────────────────────────────────────────────

/**
 * HeadroomBandDiagram (horizontal compact)
 *
 * Designed as a wide, short strip:
 *   [ ↓ down value | floor label ]  ←——— coloured bar ———→  [ ceiling label | ↑ up value ]
 * A baseline marker divides the rose (downward) zone from the emerald (upward) zone.
 * Summary stats sit in a tight row below.
 */
function HeadroomBandDiagram({
  pMin,
  pMax,
  pBaseline,
  up,
  down,
  formatVal,
}: {
  pMin: number
  pMax: number
  pBaseline: number
  up: number
  down: number
  /** Formats a display value; caller decides power vs energy. */
  formatVal: (value: number) => string
}) {
  const total = pMax - pMin
  if (total <= 0) {
    return (
      <div className="flex items-center justify-center text-muted-foreground text-xs">
        No range data
      </div>
    )
  }

  const baselineFrac = Math.min(0.98, Math.max(0.02, (pBaseline - pMin) / total))

  return (
    <div className="flex items-center gap-3 w-full">
      {/* Left: downward headroom */}
      <div className="flex flex-col items-end shrink-0 w-28">
        <span className="text-base font-bold tabular-nums text-indigo-600">{formatVal(down)}</span>
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
          <ArrowDown className="h-3 w-3" /> Downward
        </span>
        <span className="text-[10px] text-muted-foreground/60">Floor: {formatVal(pMin)}</span>
      </div>

      {/* Centre: coloured band bar */}
      <div className="relative flex-1 h-10 rounded-lg">
        <div className="absolute inset-0 rounded-lg overflow-hidden flex">
          <div
            className="h-full bg-indigo-500/25 flex-none transition-all duration-500"
            style={{ width: `${baselineFrac * 100}%` }}
          />
          <div className="h-full bg-emerald-500/25 flex-1 transition-all duration-500" />
        </div>
        <div
          className="absolute top-0 bottom-0 w-0.5 bg-foreground/30 z-10"
          style={{ left: `${baselineFrac * 100}%` }}
        />
        <div
          className="absolute top-1/2 z-10 pointer-events-none"
          style={{ left: `${baselineFrac * 100}%`, transform: 'translate(-50%, -50%)' }}
        >
          <span className="text-[9px] font-semibold text-foreground/50 uppercase tracking-widest whitespace-nowrap bg-background/80 px-1.5 py-0.5 rounded border border-border/40">
            baseline
          </span>
        </div>
      </div>

      {/* Right: upward headroom */}
      <div className="flex flex-col items-start shrink-0 w-28">
        <span className="text-base font-bold tabular-nums text-emerald-600">{formatVal(up)}</span>
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
          <ArrowUp className="h-3 w-3" /> Upward
        </span>
        <span className="text-[10px] text-muted-foreground/60">Ceiling: {formatVal(pMax)}</span>
      </div>
    </div>
  )
}

// ─── Overview view ────────────────────────────────────────────────────────────

// Early-unplug tolerance used for the overview stat card. A session must unplug
// more than this many minutes before its scheduled departure to count as early.
// Matches the default on the History tab slider so the numbers agree out of the box.
const EARLY_UNPLUG_TOLERANCE_MIN = 30

function OverviewView({
  poolID,
  poolPurpose,
  displayMode,
  onNavigate,
}: {
  poolID: string
  poolPurpose?: string
  displayMode: DisplayMode
  onNavigate: (view: View) => void
}) {
  const refreshInterval = poolPurpose === 'intraday_cloud' ? 30000 : 10000
  const { data: poolState, isLoading: stateLoading } = usePoolState(poolID, refreshInterval)
  const { data: allBands = [], isLoading: bandsLoading } = useFlexBands(poolID)
  // Yesterday (UTC, full day) sessions for session count / success rate
  const { data: sessions = [] } = useChargingSessions(poolID, 'yesterday', true, { includeMetadata: true })

  const ev = poolState?.ev as EVCurrentState | undefined
  const meter = poolState?.meter as MeterFlexibilities | undefined
  const battery = poolState?.battery as BatteryFlexibilities | undefined

  // First upcoming band — for next-interval flex stats
  const nextBand: any = useMemo(() => {
    if (allBands.length === 0) return null
    const now = Date.now()
    return allBands.find((b: any) => new Date(b.startAt).getTime() >= now) ?? null
  }, [allBands])

  const headroom = useMemo(() => {
    if (!nextBand) return null
    const b = nextBand
    const eMax = b.eMax ?? 0
    const eMin = b.eMin ?? 0
    const eBaseline = b.eBaseline ?? 0
    const upWh = Math.max(0, eMax - eBaseline)
    const downWh = Math.max(0, eBaseline - eMin)
    const totalWh = eMax - eMin
    const durationMs = new Date(b.endAt).getTime() - new Date(b.startAt).getTime()
    const toPower = (wh: number) => energyToIntervalPower(wh, durationMs)
    return {
      up: toPower(upWh),
      down: toPower(downWh),
      total: toPower(totalWh),
      pMax: toPower(eMax),
      pMin: toPower(eMin),
      pBaseline: toPower(eBaseline),
      eUp: upWh,
      eDown: downWh,
      eTotal: totalWh,
      eMax,
      eMin,
      eBaseline,
    }
  }, [nextBand])

  const fmtBand = (watts: number, wh: number) =>
    displayMode === 'power' ? formatPower(watts) : formatEnergy(wh)

  const fmt = (d: Date) => d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
  const intervalLabel = nextBand
    ? `${fmt(new Date(nextBand.startAt))} – ${fmt(new Date(nextBand.endAt))}`
    : '—'

  // Session stats for the last 24h
  const sessionCount = sessions.length

  const sessionStats = useMemo(() => {
    if (sessions.length === 0) return null

    // Scheduled = departure-time mode with a known target SoC.
    // This is the shared denominator for all three percentage stats so the
    // numbers are consistent with each other and with the session count card.
    const scheduled = sessions.filter(
      (s: any) => s.chargeMode !== 'FORCED_EV' && s.desiredSoC !== undefined
    )
    const scheduledCount = scheduled.length

    const getFeasibility = (s: any): 'feasible' | 'infeasible' | 'unknown' => {
      if (!s.scheduledDepartureTime || s.desiredSoC === undefined || s.medianChargePower === 0)
        return 'unknown'
      const socGap = s.desiredSoC - s.pluggedSoC
      if (socGap <= 0) return 'feasible'
      const plannedMs =
        new Date(s.scheduledDepartureTime).getTime() - new Date(s.pluggedAt).getTime()
      if (plannedMs <= 0) return 'infeasible'
      const plannedHours = plannedMs / 3_600_000
      const energyNeededWh = (socGap / 100) * s.maxEnergy
      const maxDeliverableWh = s.maxChargePower * plannedHours
      return energyNeededWh <= maxDeliverableWh ? 'feasible' : 'infeasible'
    }

    const isEarlyUnplug = (s: any): boolean => {
      if (!s.scheduledDepartureTime) return false
      const toleranceMs = EARLY_UNPLUG_TOLERANCE_MIN * 60 * 1000
      return (
        new Date(s.unpluggedAt) <
        new Date(new Date(s.scheduledDepartureTime).getTime() - toleranceMs)
      )
    }

    // Success rate uses server-computed evaluation from session metadata.
    // Controllable sessions are SUCCESS or FAILURE; DISCARDED and INFEASIBLE are excluded
    // from both numerator and denominator — same semantic as the old heuristic.
    // If metadata is missing from any session (shouldn't happen when requested), show nothing.
    const metaAvailable = sessions.every((s: any) => s.metadata !== undefined)

    const controllable = metaAvailable
      ? sessions.filter(
          (s: any) =>
            s.metadata.evaluation === 'SUCCESS' || s.metadata.evaluation === 'FAILURE'
        )
      : []
    const eligibleCount = controllable.length
    const successCount = metaAvailable
      ? controllable.filter((s: any) => s.metadata.evaluation === 'SUCCESS').length
      : 0
    const successRate =
      metaAvailable && eligibleCount > 0
        ? Math.round((successCount / eligibleCount) * 100)
        : null

    // Infeasible: sessions where feasibility is determinable and came back infeasible.
    // Unknown-feasibility sessions count neither as infeasible nor as feasible in
    // the numerator, but they are still part of the shared scheduled denominator.
    const infeasibleCount = scheduled.filter((s: any) => getFeasibility(s) === 'infeasible').length
    const infeasibleRate =
      scheduledCount > 0 ? Math.round((infeasibleCount / scheduledCount) * 100) : null

    // Early unplugs: scheduled sessions where the vehicle left before its scheduled departure.
    const earlyUnplugCount = scheduled.filter((s: any) => isEarlyUnplug(s)).length
    const earlyUnplugRate =
      scheduledCount > 0 ? Math.round((earlyUnplugCount / scheduledCount) * 100) : null

    // Average session duration across all sessions (including forced-mode)
    const durationsH = sessions
      .map((s: any) => {
        const ms = new Date(s.unpluggedAt).getTime() - new Date(s.pluggedAt).getTime()
        return ms / 3_600_000
      })
      .filter((h: number) => h > 0)
    const avgDurationH =
      durationsH.length > 0
        ? durationsH.reduce((a: number, b: number) => a + b, 0) / durationsH.length
        : null

    return {
      scheduledCount,
      eligibleCount,
      successRate,
      successCount,
      infeasibleRate,
      infeasibleCount,
      earlyUnplugCount,
      earlyUnplugRate,
      avgDurationH,
    }
  }, [sessions])

  const bandsAvailable = !bandsLoading && allBands.length > 0

  // ── Forecast stats derived from all upcoming bands ──────────────────────────
  const forecastStats = useMemo(() => {
    const now = Date.now()
    const upcoming = allBands.filter((b: any) => new Date(b.startAt).getTime() >= now)
    if (upcoming.length === 0) return null

    const h24 = now + 24 * 3_600_000
    const h48 = now + 48 * 3_600_000

    // 1. Baseline energy sums for next 24h and 48h
    let eBaseline24 = 0
    let eBaseline48 = 0
    for (const b of upcoming as any[]) {
      const start = new Date(b.startAt).getTime()
      if (start < h24) eBaseline24 += b.eBaseline ?? 0
      if (start < h48) eBaseline48 += b.eBaseline ?? 0
    }

    // 2. Peak flex window — best 4-interval (1h) rolling block by total upward flex
    const upwardByInterval = (upcoming as any[]).map((b) =>
      Math.max(0, (b.eMax ?? 0) - (b.eBaseline ?? 0))
    )
    let bestWindowStart = 0
    let bestWindowUp = -Infinity
    const windowSize = 4 // 4 × 15-min = 1 hour
    for (let i = 0; i <= upwardByInterval.length - windowSize; i++) {
      const sum = upwardByInterval.slice(i, i + windowSize).reduce((a, v) => a + v, 0)
      if (sum > bestWindowUp) {
        bestWindowUp = sum
        bestWindowStart = i
      }
    }
    const peakWindowBand: any = upcoming[bestWindowStart]
    const peakWindowTime = peakWindowBand
      ? new Date(peakWindowBand.startAt).toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          day: upcoming[bestWindowStart] ? undefined : undefined,
        })
      : null
    // Include day hint if the peak window is not today
    const peakWindowDate = peakWindowBand ? new Date(peakWindowBand.startAt) : null
    const today = new Date()
    const peakWindowDayLabel =
      peakWindowDate && peakWindowDate.getDate() !== today.getDate()
        ? peakWindowDate.toLocaleDateString('en-US', { weekday: 'short' }) + ' '
        : ''
    const peakWindowLabel = peakWindowBand ? `${peakWindowDayLabel}${peakWindowTime}` : null

    // 3. Flex balance — upward vs downward share over next 24h
    let totalUp24 = 0
    let totalDown24 = 0
    for (const b of upcoming as any[]) {
      if (new Date(b.startAt).getTime() >= h24) break
      totalUp24 += Math.max(0, (b.eMax ?? 0) - (b.eBaseline ?? 0))
      totalDown24 += Math.max(0, (b.eBaseline ?? 0) - (b.eMin ?? 0))
    }
    const totalFlex24 = totalUp24 + totalDown24
    const upwardSharePct = totalFlex24 > 0 ? Math.round((totalUp24 / totalFlex24) * 100) : null

    // 4. Constrained intervals — intervals in next 24h with flex < 20% of median total flex
    const upcoming24 = (upcoming as any[]).filter((b) => new Date(b.startAt).getTime() < h24)
    const flexValues = upcoming24.map((b) => (b.eMax ?? 0) - (b.eMin ?? 0))
    const sorted = [...flexValues].sort((a, b) => a - b)
    const median = sorted.length > 0 ? sorted[Math.floor(sorted.length / 2)] : 0
    const threshold = median * 0.2
    const constrainedBands = upcoming24.filter(
      (_b, i) => flexValues[i] < threshold && threshold > 0
    )
    const constrainedCount = constrainedBands.length
    const firstConstrainedTime =
      constrainedBands.length > 0
        ? new Date(constrainedBands[0].startAt).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
          })
        : null

    // 5. Expected baseline cost — sum of eBaseline × daPriceEurKwh / 1000 over 24h / 48h
    //    Only computed when at least one band has price data; null otherwise.
    let expectedCost24: number | null = null
    let expectedCost48: number | null = null
    let hasPriceData = false
    for (const b of upcoming as any[]) {
      if (b.daPriceEurKwh === undefined) continue
      hasPriceData = true
      const cost = ((b.eBaseline ?? 0) * b.daPriceEurKwh) / 1000
      const start = new Date(b.startAt).getTime()
      if (start < h24) expectedCost24 = (expectedCost24 ?? 0) + cost
      if (start < h48) expectedCost48 = (expectedCost48 ?? 0) + cost
    }
    if (!hasPriceData) {
      expectedCost24 = null
      expectedCost48 = null
    }

    // 6. Price spread · 24h — difference between highest and lowest DA price in next 24h
    const prices24 = (upcoming as any[])
      .filter((b) => new Date(b.startAt).getTime() < h24 && b.daPriceEurKwh !== undefined)
      .map((b) => ({ price: b.daPriceEurKwh as number, startAt: b.startAt as string }))
    let priceSpread: number | null = null
    let lowestPriceTime: string | null = null
    let highestPriceTime: string | null = null
    if (prices24.length >= 2) {
      const minEntry = prices24.reduce((a, b) => (b.price < a.price ? b : a))
      const maxEntry = prices24.reduce((a, b) => (b.price > a.price ? b : a))
      priceSpread = maxEntry.price - minEntry.price
      lowestPriceTime = new Date(minEntry.startAt).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
      })
      highestPriceTime = new Date(maxEntry.startAt).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
      })
    }

    return {
      eBaseline24,
      eBaseline48,
      bestWindowUp,
      peakWindowLabel,
      totalUp24,
      totalDown24,
      upwardSharePct,
      constrainedCount,
      firstConstrainedTime,
      expectedCost24,
      expectedCost48,
      priceSpread,
      lowestPriceTime,
      highestPriceTime,
    }
  }, [allBands])

  return (
    <div className="h-full flex flex-col gap-6 p-5 overflow-auto">
      {/* Section: Live state */}
      <div>
        <p className="text-[10px] font-semibold text-sky-500 uppercase tracking-wider mb-2.5">
          Live State
        </p>
        <div className="grid grid-cols-3 gap-3">
          <StatTile
            icon={<Car className="h-5 w-5" />}
            label="EV Active Power"
            info="Real-time aggregate charge power of all connected EVs. The sub-label shows the setpoint the controller has commanded the chargers to deliver."
            value={ev ? formatPower(ev.activePower) : '—'}
            sub={ev ? `Setpoint: ${formatPower(ev.activePowerSetpoint)}` : undefined}
            accent="blue"
            loading={stateLoading && !poolState}
          />
          {poolPurpose !== 'intraday_cloud' && (
            <StatTile
              icon={<Zap className="h-5 w-5" />}
              label="Meter Power"
              info="Power measured at the grid connection point. May include loads other than EVs (e.g. building consumption). The sub-label shows energy consumed in the current 15-minute settlement window."
              value={meter ? formatPower(meter.activePower) : '—'}
              sub={meter ? `15m: ${formatEnergy(meter.aggregatedEnergyConsumption)}` : undefined}
              accent="sky"
              loading={stateLoading && !poolState}
            />
          )}
          {battery && (
            <StatTile
              icon={<Activity className="h-5 w-5" />}
              label="Battery SoC"
              info="State of charge of the stationary battery asset. The sub-label shows its current active charge/discharge power."
              value={`${battery.stateOfCharge?.toFixed(1) ?? '—'}%`}
              sub={`${formatPower(battery.activePower)}`}
              accent={
                battery.stateOfCharge > 70
                  ? 'emerald'
                  : battery.stateOfCharge > 30
                    ? 'amber'
                    : 'rose'
              }
              loading={stateLoading && !poolState}
            />
          )}
        </div>
      </div>

      {/* Section: Charging sessions */}
      <div>
        <p className="text-[10px] font-semibold text-violet-500 uppercase tracking-wider mb-2.5">
          Charging Sessions · Yesterday
        </p>
        <div className="grid grid-cols-5 gap-3">
          <StatTile
            icon={<PlugZap className="h-5 w-5" />}
            label="Sessions"
            info="Total charging sessions on yesterday (UTC). The sub-label shows how many were scheduled (departure-time mode with a target SoC) — the basis for the percentage stats below."
            value={String(sessionCount)}
            sub={sessionStats ? `${sessionStats.scheduledCount} scheduled` : undefined}
            accent="violet"
            onClick={() => onNavigate('history')}
          />
          <StatTile
            icon={<CheckCircle2 className="h-5 w-5" />}
            label="Success Rate"
            info={
              <>
                <p className="font-medium mb-1">Success rate of scheduled sessions</p>
                <p>
                  Share of sessions evaluated as <strong>SUCCESS</strong> out of all controllable
                  sessions (SUCCESS + FAILURE). Discarded (forced-charge) and infeasible/early-unplug
                  sessions are excluded from both numerator and denominator. Evaluation is computed
                  server-side and returned via session metadata.
                </p>
              </>
            }
            value={sessionStats?.successRate != null ? `${sessionStats.successRate}%` : '—'}
            sub={
              sessionStats?.eligibleCount
                ? `${sessionStats.successCount} of ${sessionStats.eligibleCount} eligible`
                : undefined
            }
            accent={
              sessionStats?.successRate == null
                ? 'violet'
                : sessionStats.successRate >= 90
                  ? 'emerald'
                  : sessionStats.successRate >= 70
                    ? 'amber'
                    : 'rose'
            }
            onClick={() => onNavigate('history')}
          />
          <StatTile
            icon={<XCircle className="h-5 w-5" />}
            label="Infeasible Sessions"
            info={
              <>
                <p className="font-medium mb-1">Structurally infeasible charging requests</p>
                <p>
                  Share of scheduled sessions where the target SoC could <strong>not</strong> have
                  been reached within the planned duration even at maximum charge power.
                </p>
                <p className="mt-1 text-muted-foreground">
                  Uses the same denominator as the session count (all scheduled sessions). A high
                  rate suggests EVs are being plugged in too late or targets are unrealistically
                  high for the available window. Sessions where feasibility cannot be determined (no
                  departure time or zero median power) count as neither feasible nor infeasible but
                  are included in the denominator.
                </p>
              </>
            }
            value={sessionStats?.infeasibleRate != null ? `${sessionStats.infeasibleRate}%` : '—'}
            sub={
              sessionStats?.scheduledCount
                ? `${sessionStats.infeasibleCount} of ${sessionStats.scheduledCount} scheduled`
                : undefined
            }
            accent={
              sessionStats?.infeasibleRate == null
                ? 'violet'
                : sessionStats.infeasibleRate === 0
                  ? 'emerald'
                  : sessionStats.infeasibleRate <= 15
                    ? 'amber'
                    : 'rose'
            }
            onClick={() => onNavigate('history')}
          />
          <StatTile
            icon={<LogOut className="h-5 w-5" />}
            label="Early Unplugs"
            info={
              <>
                <p className="font-medium mb-1">Sessions unplugged before scheduled departure</p>
                <p>
                  Share of scheduled sessions (departure-time mode, desired SoC known) where the
                  vehicle left before its scheduled departure time. Early unplugs are outside the
                  controller's control and are excluded from the success rate denominator.
                </p>
              </>
            }
            value={sessionStats?.earlyUnplugRate != null ? `${sessionStats.earlyUnplugRate}%` : '—'}
            sub={
              sessionStats?.scheduledCount
                ? `${sessionStats.earlyUnplugCount} of ${sessionStats.scheduledCount} scheduled`
                : undefined
            }
            accent={
              sessionStats?.earlyUnplugRate == null
                ? 'violet'
                : sessionStats.earlyUnplugRate === 0
                  ? 'emerald'
                  : sessionStats.earlyUnplugRate <= 15
                    ? 'amber'
                    : 'rose'
            }
            onClick={() => onNavigate('history')}
          />
          <StatTile
            icon={<Gauge className="h-5 w-5" />}
            label="Avg Session Duration"
            info={
              <>
                <p className="font-medium mb-1">Average plug-in duration</p>
                <p>
                  Mean time between plug-in and unplug across all sessions on yesterday (UTC),
                  including forced-mode sessions.
                </p>
                <p className="mt-1 text-muted-foreground">
                  Short average durations reduce the controller's ability to shift load — there is
                  less time to smooth charging across the session window.
                </p>
              </>
            }
            value={
              sessionStats?.avgDurationH != null
                ? sessionStats.avgDurationH >= 1
                  ? `${sessionStats.avgDurationH.toFixed(1)}h`
                  : `${Math.round(sessionStats.avgDurationH * 60)}m`
                : '—'
            }
            accent="violet"
            onClick={() => onNavigate('history')}
          />
        </div>
      </div>

      {/* Section: Next interval flex */}
      <div>
        <p className="text-[10px] font-semibold text-teal-500 uppercase tracking-wider mb-2.5">
          Next Interval{intervalLabel !== '—' ? ` · ${intervalLabel}` : ''}
        </p>
        <div className="grid grid-cols-4 gap-3">
          <StatTile
            icon={<ArrowDown className="h-5 w-5" />}
            label={displayMode === 'power' ? 'Baseline Power' : 'Baseline Energy'}
            info={
              displayMode === 'power'
                ? 'Expected average power for the next interval if no dispatch command is sent.'
                : 'Expected energy consumption for the next interval if no dispatch command is sent.'
            }
            value={headroom ? fmtBand(headroom.pBaseline, headroom.eBaseline) : '—'}
            accent="teal"
            loading={bandsLoading && !bandsAvailable}
            onClick={() => onNavigate('dispatch')}
          />
          <StatTile
            icon={<ArrowUp className="h-5 w-5" />}
            label={displayMode === 'power' ? 'Upward Flex' : 'Upward Flex (energy)'}
            info={
              displayMode === 'power'
                ? 'Available upward flexibility for the next interval: how much average power the pool can be pushed above baseline (pMax − pBaseline).'
                : 'Available upward flexibility for the next interval: energy above baseline the pool can absorb (eMax − eBaseline).'
            }
            value={headroom ? fmtBand(headroom.up, headroom.eUp) : '—'}
            accent="emerald"
            loading={bandsLoading && !bandsAvailable}
            onClick={() => onNavigate('dispatch')}
          />
          <StatTile
            icon={<ArrowDown className="h-5 w-5" />}
            label={displayMode === 'power' ? 'Downward Flex' : 'Downward Flex (energy)'}
            info={
              displayMode === 'power'
                ? 'Available downward flexibility for the next interval: how much average power the pool can be pulled below baseline (pBaseline − pMin).'
                : 'Available downward flexibility for the next interval: energy below baseline the pool can shed (eBaseline − eMin).'
            }
            value={headroom ? fmtBand(headroom.down, headroom.eDown) : '—'}
            accent="teal"
            loading={bandsLoading && !bandsAvailable}
            onClick={() => onNavigate('dispatch')}
          />
          <StatTile
            icon={<Activity className="h-5 w-5" />}
            label={displayMode === 'power' ? 'Floor Power' : 'Floor Energy'}
            info={
              displayMode === 'power'
                ? 'Minimum average power the pool must maintain in the next interval (mandatory floor — cannot be dispatched below this).'
                : 'Minimum energy the pool must draw in the next interval (mandatory floor — cannot be dispatched below this).'
            }
            value={headroom ? fmtBand(headroom.pMin, headroom.eMin) : '—'}
            accent="teal"
            loading={bandsLoading && !bandsAvailable}
            onClick={() => onNavigate('dispatch')}
          />
        </div>
      </div>

      {/* Section: Flexibility Band */}
      <div>
        <p className="text-[10px] font-semibold text-orange-500 uppercase tracking-wider mb-2.5">
          Flexibility Band
        </p>
        <div className="grid grid-cols-3 gap-3">
          {/* Baseline energy: 24h + 48h combined */}
          <StatTile
            icon={<BatteryCharging className="h-5 w-5" />}
            label="Expected Load"
            info={
              <>
                <p className="font-medium mb-1">Baseline energy demand</p>
                <p>
                  Total energy the pool is forecast to consume if no dispatch commands are issued —
                  the unconstrained baseline.
                </p>
                <p className="mt-1 text-muted-foreground">
                  Useful for planning grid capacity and comparing against contracted volumes.
                </p>
              </>
            }
            value={forecastStats ? formatEnergy(forecastStats.eBaseline24) : '—'}
            sub={forecastStats ? `48h: ${formatEnergy(forecastStats.eBaseline48)}` : undefined}
            accent="orange"
            loading={bandsLoading && !bandsAvailable}
            onClick={() => onNavigate('forecast')}
          />
          {/* Peak flex window */}
          <StatTile
            icon={<Star className="h-5 w-5" />}
            label="Peak Flex Window"
            info={
              <>
                <p className="font-medium mb-1">Best 1-hour dispatch opportunity</p>
                <p>
                  The upcoming 1-hour block (4 × 15-min intervals) with the greatest total upward
                  flexibility — the optimal window to schedule a large charge command.
                </p>
                <p className="mt-1 text-muted-foreground">
                  Value shown is total upward headroom energy available in that block.
                </p>
              </>
            }
            value={forecastStats ? formatEnergy(forecastStats.bestWindowUp) : '—'}
            sub={forecastStats?.peakWindowLabel ?? undefined}
            accent="orange"
            loading={bandsLoading && !bandsAvailable}
            onClick={() => onNavigate('forecast')}
          />
          {/* Flex balance */}
          <StatTile
            icon={<Scale className="h-5 w-5" />}
            label="Flex Balance · 24h"
            info={
              <>
                <p className="font-medium mb-1">Upward vs downward flexibility split</p>
                <p>
                  Share of total flexibility headroom that is upward (load increase) versus downward
                  (load reduction / discharge) over the next 24 hours.
                </p>
                <p className="mt-1 text-muted-foreground">
                  A heavily skewed pool may struggle to respond to commands in one direction.
                  Balanced pools offer the most symmetric response capability.
                </p>
              </>
            }
            value={
              forecastStats?.upwardSharePct != null ? `${forecastStats.upwardSharePct}% ↑` : '—'
            }
            sub={
              forecastStats?.upwardSharePct != null
                ? `${100 - forecastStats.upwardSharePct}% ↓ downward`
                : undefined
            }
            accent={
              forecastStats?.upwardSharePct == null
                ? 'orange'
                : forecastStats.upwardSharePct >= 40 && forecastStats.upwardSharePct <= 60
                  ? 'emerald'
                  : forecastStats.upwardSharePct >= 25 && forecastStats.upwardSharePct <= 75
                    ? 'amber'
                    : 'rose'
            }
            loading={bandsLoading && !bandsAvailable}
            onClick={() => onNavigate('forecast')}
          />
          {/* Constrained intervals */}
          <StatTile
            icon={<AlertTriangle className="h-5 w-5" />}
            label="Tight Intervals · 24h"
            info={
              <>
                <p className="font-medium mb-1">Constrained dispatch windows</p>
                <p>
                  Number of 15-minute intervals in the next 24 hours where total flexibility (floor
                  to ceiling) is less than 20% of the median — periods where the pool has very
                  little room to respond to a dispatch command.
                </p>
                <p className="mt-1 text-muted-foreground">
                  High counts may indicate the pool is nearly fully committed and can't absorb
                  unexpected grid signals.
                </p>
              </>
            }
            value={forecastStats ? String(forecastStats.constrainedCount) : '—'}
            sub={
              forecastStats?.constrainedCount
                ? forecastStats.firstConstrainedTime
                  ? `First at ${forecastStats.firstConstrainedTime}`
                  : undefined
                : forecastStats
                  ? 'None — full headroom ahead'
                  : undefined
            }
            accent={
              forecastStats == null
                ? 'orange'
                : forecastStats.constrainedCount === 0
                  ? 'emerald'
                  : forecastStats.constrainedCount <= 3
                    ? 'amber'
                    : 'rose'
            }
            loading={bandsLoading && !bandsAvailable}
            onClick={() => onNavigate('forecast')}
          />
          {/* Expected baseline cost */}
          <StatTile
            icon={<CircleDollarSign className="h-5 w-5" />}
            label="Expected Cost · 24h"
            info={
              <>
                <p className="font-medium mb-1">Forecast baseline electricity cost</p>
                <p>
                  Expected cost of the pool's baseline energy consumption over the next 24 hours,
                  computed as the sum of each interval's baseline energy (Wh) multiplied by the
                  day-ahead price (EUR/kWh).
                </p>
                <p className="mt-1 text-muted-foreground">
                  Only shown when DA price data is available. Does not include dispatch commands —
                  reflects the unconstrained baseline scenario.
                </p>
              </>
            }
            value={
              forecastStats?.expectedCost24 != null
                ? `€${forecastStats.expectedCost24.toFixed(2)}`
                : '—'
            }
            sub={
              forecastStats?.expectedCost48 != null
                ? `48h: €${forecastStats.expectedCost48.toFixed(2)}`
                : undefined
            }
            accent="amber"
            loading={bandsLoading && !bandsAvailable}
            onClick={() => onNavigate('forecast')}
          />
          {/* Price spread */}
          <StatTile
            icon={<TrendingDown className="h-5 w-5" />}
            label="Price Spread · 24h"
            info={
              <>
                <p className="font-medium mb-1">Day-ahead price range</p>
                <p>
                  Difference between the highest and lowest day-ahead electricity price across all
                  intervals in the next 24 hours.
                </p>
                <p className="mt-1 text-muted-foreground">
                  A large spread indicates strong arbitrage potential — the pool can shift load from
                  expensive to cheap intervals for maximum cost savings.
                </p>
              </>
            }
            value={
              forecastStats?.priceSpread != null
                ? `€${(forecastStats.priceSpread * 1000).toFixed(2)}/MWh`
                : '—'
            }
            sub={
              forecastStats?.lowestPriceTime != null && forecastStats?.highestPriceTime != null
                ? `Low ${forecastStats.lowestPriceTime} · High ${forecastStats.highestPriceTime}`
                : undefined
            }
            accent={
              forecastStats?.priceSpread == null
                ? 'orange'
                : forecastStats.priceSpread * 1000 < 20
                  ? 'emerald'
                  : forecastStats.priceSpread * 1000 < 60
                    ? 'amber'
                    : 'rose'
            }
            loading={bandsLoading && !bandsAvailable}
            onClick={() => onNavigate('forecast')}
          />
        </div>
      </div>
    </div>
  )
}

// ─── Battery overview view ────────────────────────────────────────────────────
//
// Shown instead of OverviewView for imbalance / tou_stacking_poc pools.
// Contains only live battery and meter state — no forecast, sessions, or flex
// bands, since those concepts don't apply to battery pools.

function BatteryOverviewView({ poolID }: { poolID: string }) {
  const [autoRefresh, setAutoRefresh] = useState(false)
  const refreshInterval = 10000
  const {
    data: poolState,
    isLoading,
    isFetching,
    refetch,
  } = usePoolState(poolID, autoRefresh ? refreshInterval : false)

  const battery = poolState?.battery as BatteryFlexibilities | undefined
  const meter = poolState?.meter as MeterFlexibilities | undefined
  const pv = poolState?.pv as PVFlexibilities | undefined

  return (
    <div className="h-full flex flex-col gap-0 overflow-hidden">
      {/* Header bar */}
      <div className="flex items-start justify-between px-5 py-4 shrink-0 border-b">
        <div>
          <h2 className="text-lg font-semibold">Real-Time Pool State</h2>
          <p className="text-sm text-muted-foreground">Live monitoring</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={autoRefresh ? 'default' : 'outline'}
            size="sm"
            onClick={() => setAutoRefresh((v) => !v)}
            className="h-8 text-xs"
          >
            <RefreshCw className={cn('h-3 w-3 mr-1.5', autoRefresh && 'animate-spin')} />
            Auto-refresh {autoRefresh ? 'ON' : 'OFF'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
            className="h-8 w-8 p-0"
          >
            {isFetching ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" />
            )}
          </Button>
        </div>
      </div>

      {/* Asset cards */}
      {isLoading && !poolState ? (
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="flex-1 flex gap-5 p-5 overflow-auto">
          {/* Battery card */}
          {battery && (
            <div className="flex-1 rounded-xl border bg-card p-5 flex flex-col gap-4 self-start">
              <h3 className="flex items-center gap-2 font-semibold text-base">
                <Battery className="h-5 w-5" />
                Battery
              </h3>

              {/* State of Charge */}
              {battery.stateOfCharge !== undefined && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="flex items-center gap-1 text-sm text-muted-foreground">
                      State of Charge
                      <InfoTip content="Battery charge level as a percentage of rated capacity. The amber zones at each end mark the configured min/max SoC limits." />
                    </span>
                    <span className="text-2xl font-bold">
                      {battery.stateOfCharge.toFixed(1)}%
                      {battery.minStateOfCharge !== undefined &&
                        battery.maxStateOfCharge !== undefined && (
                          <span className="text-sm font-normal text-muted-foreground ml-2">
                            ({parseFloat(battery.minStateOfCharge.toFixed(2))}% -{' '}
                            {parseFloat(battery.maxStateOfCharge.toFixed(2))}%)
                          </span>
                        )}
                    </span>
                  </div>
                  <div className="relative w-full bg-muted rounded-full h-2">
                    {battery.minStateOfCharge !== undefined && (
                      <div
                        className="absolute h-2 bg-amber-500/20 rounded-l-full"
                        style={{ width: `${battery.minStateOfCharge}%` }}
                      />
                    )}
                    {battery.maxStateOfCharge !== undefined && battery.maxStateOfCharge < 100 && (
                      <div
                        className="absolute h-2 bg-amber-500/20 rounded-r-full right-0"
                        style={{ width: `${100 - battery.maxStateOfCharge}%` }}
                      />
                    )}
                    <div
                      className="absolute h-2 bg-green-500 rounded-full transition-all"
                      style={{ width: `${battery.stateOfCharge}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Power section */}
              <div className="border-t pt-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                  Power
                </p>
                <div className="space-y-3">
                  {battery.activePower !== undefined && (
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-1 text-sm text-muted-foreground">
                        Active Power
                        <InfoTip content="Real-time charge (+) or discharge (−) power of the battery at this moment." />
                      </span>
                      <span className="font-semibold">{formatPower(battery.activePower)}</span>
                    </div>
                  )}
                  {battery.activePowerSetpoint !== undefined && (
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-1 text-sm text-muted-foreground">
                        Power Setpoint
                        <InfoTip content="The power level the controller has commanded the battery to target. Positive = charge, negative = discharge." />
                      </span>
                      <span className="font-semibold">
                        {formatPower(battery.activePowerSetpoint)}
                      </span>
                    </div>
                  )}
                  {battery.maxChargePower !== undefined && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-1.5 text-muted-foreground/80">
                        <Zap className="h-3.5 w-3.5" />
                        Max Charge Power
                        <InfoTip content="Hardware-rated maximum charge rate." />
                      </span>
                      <span className="font-medium text-muted-foreground">
                        {formatPower(battery.maxChargePower)}
                      </span>
                    </div>
                  )}
                  {battery.maxDischargePower !== undefined && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-1.5 text-muted-foreground/80">
                        <Zap className="h-3.5 w-3.5" />
                        Max Discharge Power
                        <InfoTip content="Hardware-rated maximum discharge rate." />
                      </span>
                      <span className="font-medium text-muted-foreground">
                        {formatPower(battery.maxDischargePower)}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Energy section */}
              <div className="border-t pt-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                  Energy
                </p>
                <div className="space-y-3">
                  {battery.currentEnergy !== undefined && battery.maxEnergy !== undefined && (
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="flex items-center gap-1 text-sm text-muted-foreground">
                          Energy Capacity
                          <InfoTip content="Current stored energy vs. the battery's rated maximum capacity." />
                        </span>
                        <span className="font-semibold">
                          {formatEnergy(battery.currentEnergy)} / {formatEnergy(battery.maxEnergy)}
                        </span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-1.5">
                        <div
                          className="bg-blue-500 h-1.5 rounded-full transition-all"
                          style={{
                            width: `${(battery.currentEnergy / battery.maxEnergy) * 100}%`,
                          }}
                        />
                      </div>
                    </div>
                  )}
                  {battery.aggregatedEnergyConsumption !== undefined && (
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-1 text-sm text-muted-foreground">
                        Energy Consumption (15m)
                        <InfoTip content="Energy drawn from the grid by the battery in the current 15-minute settlement window." />
                      </span>
                      <span className="font-semibold">
                        {formatEnergy(battery.aggregatedEnergyConsumption)}
                      </span>
                    </div>
                  )}
                  {battery.aggregatedEnergyProduction !== undefined && (
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-1 text-sm text-muted-foreground">
                        Energy Production (15m)
                        <InfoTip content="Energy exported to the grid by the battery in the current 15-minute settlement window." />
                      </span>
                      <span className="font-semibold">
                        {formatEnergy(battery.aggregatedEnergyProduction)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Meter card */}
          {meter && (
            <div className="flex-1 rounded-xl border bg-card p-5 flex flex-col gap-4 self-start">
              <div>
                <h3 className="flex items-center gap-2 font-semibold text-base">
                  <Zap className="h-5 w-5" />
                  Meter
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Measured at the grid connection point. This can include assets other than the
                  battery, such as PV and household load.
                </p>
              </div>

              <div className="space-y-3">
                {meter.activePower !== undefined && (
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-1 text-sm text-muted-foreground">
                      Active Power
                      <InfoTip content="Real-time power measured at the grid connection point. Positive = consuming from grid, negative = exporting." />
                    </span>
                    <span className="font-semibold">{formatPower(meter.activePower)}</span>
                  </div>
                )}
                {meter.aggregatedEnergyConsumption !== undefined ? (
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-1 text-sm text-muted-foreground">
                      Energy Consumption (15min)
                      <InfoTip content="Total energy drawn from the grid in the current 15-minute settlement window." />
                    </span>
                    <span className="font-semibold">
                      {formatEnergy(meter.aggregatedEnergyConsumption)}
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-1 text-sm text-muted-foreground">
                      Energy Consumption (15min)
                      <InfoTip content="Total energy drawn from the grid in the current 15-minute settlement window." />
                    </span>
                    <span className="font-semibold text-muted-foreground">—</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* PV card */}
          {pv && (
            <div className="flex-1 rounded-xl border bg-card p-5 flex flex-col gap-4 self-start">
              <div>
                <h3 className="flex items-center gap-2 font-semibold text-base">
                  <Sun className="h-5 w-5" />
                  PV
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Aggregated photovoltaic production across the pool.
                </p>
              </div>

              <div className="space-y-3">
                {pv.activePower !== undefined && (
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-1 text-sm text-muted-foreground">
                      Active Power
                      <InfoTip content="Aggregated PV active power. Negative values indicate production (feed-in)." />
                    </span>
                    <span className="font-semibold">{formatPower(pv.activePower)}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Spacer to prevent cards from stretching to full width when only 1–2 cards */}
          <div className="flex-1" />
        </div>
      )}
    </div>
  )
}

// ─── Dispatch view ────────────────────────────────────────────────────────────

// Band hours options for the upcoming preview card
const BAND_HOUR_OPTIONS = [1, 3, 6, 12] as const
type BandHours = (typeof BAND_HOUR_OPTIONS)[number]

function DispatchView({
  poolID,
  poolPurpose,
  displayMode,
  setDisplayMode,
}: {
  poolID: string
  poolPurpose?: string
  displayMode: DisplayMode
  setDisplayMode: (mode: DisplayMode) => void
}) {
  const [autoRefresh, setAutoRefresh] = useState(false)
  const [bandHours, setBandHours] = useState<BandHours>(3)
  // null = show the live current interval; number = index into upcomingBands
  const [selectedBandIndex, setSelectedBandIndex] = useState<number | null>(null)
  const refreshInterval = poolPurpose === 'intraday_cloud' ? 30000 : 10000
  const { isFetching, refetch } = usePoolState(poolID, autoRefresh ? refreshInterval : false)
  const { data: allBands = [], isLoading: bandsLoading } = useFlexBands(poolID)

  // Upcoming bands: intervals that haven't started yet (startAt >= now).
  const upcomingBands = useMemo(() => {
    if (allBands.length === 0) return []
    const now = Date.now()
    return allBands.filter((b: any) => new Date(b.startAt).getTime() >= now).slice(0, bandHours * 4) // 4 × 15-min intervals per hour
  }, [allBands, bandHours])

  // Band driving the headroom card — pinned selection or first upcoming band
  const displayBand: any = useMemo(() => {
    if (selectedBandIndex !== null && upcomingBands[selectedBandIndex]) {
      return upcomingBands[selectedBandIndex]
    }
    return upcomingBands[0] ?? null
  }, [selectedBandIndex, upcomingBands])

  const headroom = useMemo(() => {
    if (!displayBand) return null
    const b = displayBand
    const eMax = b.eMax ?? 0
    const eMin = b.eMin ?? 0
    const eBaseline = b.eBaseline ?? 0
    const upWh = Math.max(0, eMax - eBaseline)
    const downWh = Math.max(0, eBaseline - eMin)
    const totalWh = eMax - eMin
    const maxSide = Math.max(upWh, downWh, 1)
    const durationMs = new Date(b.endAt).getTime() - new Date(b.startAt).getTime()
    const toPower = (wh: number) => energyToIntervalPower(wh, durationMs)
    const price: number | undefined = b.daPriceEurKwh
    return {
      // raw energy (Wh) — used for proportional sizing (unchanged by display mode)
      upWh,
      downWh,
      totalWh,
      upPct: (upWh / maxSide) * 100,
      downPct: (downWh / maxSide) * 100,
      // power (W)
      up: toPower(upWh),
      down: toPower(downWh),
      total: toPower(totalWh),
      pMax: toPower(eMax),
      pMin: toPower(eMin),
      pBaseline: toPower(eBaseline),
      // energy (Wh)
      eUp: upWh,
      eDown: downWh,
      eTotal: totalWh,
      eMax,
      eMin,
      eBaseline,
      // DA price context
      daPriceEurKwh: price,
      // Cost of dispatching full upward headroom (buying eUp Wh at DA price)
      upDispatchCostEur: price !== undefined ? (upWh * price) / 1000 : undefined,
      // Cost saving from curtailing full downward headroom (avoiding eDown Wh at DA price)
      downDispatchCostEur: price !== undefined ? (downWh * price) / 1000 : undefined,
    }
  }, [displayBand])

  // Format a value according to the current display mode
  const fmtBand = (watts: number, wh: number) =>
    displayMode === 'power' ? formatPower(watts) : formatEnergy(wh)

  const fmt = (d: Date) => d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })

  const intervalLabel = displayBand
    ? `${fmt(new Date(displayBand.startAt))} – ${fmt(new Date(displayBand.endAt))}`
    : '—'

  // Countdown to the start of the next interval (= end of the current in-progress one)
  const nextIntervalMs = upcomingBands[0]
    ? new Date((upcomingBands[0] as any).startAt).getTime()
    : null
  const countdown = useCountdown(nextIntervalMs)

  const maxBandVal = useMemo(
    () => Math.max(1, ...upcomingBands.map((b: any) => b.eMax ?? 0)),
    [upcomingBands]
  )

  return (
    <div className="h-full flex flex-col gap-4 p-4 overflow-hidden">
      {/* Power / Energy toggle */}
      <div className="flex rounded-md border bg-muted/40 p-0.5 w-fit shrink-0">
        <button
          onClick={() => setDisplayMode('power')}
          className={cn(
            'px-3 py-1 rounded-sm text-xs font-medium transition-all',
            displayMode === 'power'
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          )}
        >
          Power
        </button>
        <button
          onClick={() => setDisplayMode('energy')}
          className={cn(
            'px-3 py-1 rounded-sm text-xs font-medium transition-all',
            displayMode === 'energy'
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          )}
        >
          Energy
        </button>
      </div>

      {/* Dispatch Headroom — full-width horizontal card */}
      <div className="rounded-xl border bg-card px-5 py-4 shrink-0">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <h3 className="flex items-center gap-1 font-semibold text-sm">
              Dispatch Headroom
              <InfoTip
                content={
                  <>
                    <p className="font-medium mb-1">Dispatch Headroom</p>
                    <p>
                      How much average power can be shifted above or below the baseline in the
                      selected 15-minute interval (derived from interval energy ÷ duration).
                    </p>
                    <p className="mt-1">
                      <strong>Upward</strong> = room to consume more (pMax − pBaseline).{' '}
                      <strong>Downward</strong> = room to consume less or discharge (pBaseline −
                      pMin).
                    </p>
                  </>
                }
              />
            </h3>
            <span className="text-xs text-muted-foreground">
              {selectedBandIndex === null || selectedBandIndex === 0 ? (
                <>
                  Next interval · starts in{' '}
                  <span className="font-mono font-medium">{countdown || '—'}</span>
                  {intervalLabel !== '—' && (
                    <span className="ml-1 text-muted-foreground/60">({intervalLabel})</span>
                  )}
                </>
              ) : (
                <span className="font-medium text-foreground/70">{intervalLabel}</span>
              )}
            </span>
            {selectedBandIndex !== null && selectedBandIndex !== 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedBandIndex(null)}
                className="h-6 text-xs px-2"
              >
                ← Next
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant={autoRefresh ? 'default' : 'outline'}
              size="sm"
              onClick={() => setAutoRefresh((v) => !v)}
              className="h-7 text-xs px-2"
              title={`Auto-refresh ${autoRefresh ? 'on' : 'off'} — polls every ${poolPurpose === 'intraday_cloud' ? '30' : '10'} seconds`}
            >
              <RefreshCw className={cn('h-3 w-3 mr-1', autoRefresh && 'animate-spin')} />
              {autoRefresh ? 'Live' : 'Paused'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isFetching}
              className="h-7 w-7 p-0"
            >
              {isFetching ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <RefreshCw className="h-3 w-3" />
              )}
            </Button>
          </div>
        </div>

        {!headroom ? (
          <div className="flex items-center justify-center py-4 text-muted-foreground text-sm">
            {bandsLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'No flex band data'}
          </div>
        ) : (
          <>
            <HeadroomBandDiagram
              pMin={displayMode === 'power' ? headroom.pMin : headroom.eMin}
              pMax={displayMode === 'power' ? headroom.pMax : headroom.eMax}
              pBaseline={displayMode === 'power' ? headroom.pBaseline : headroom.eBaseline}
              up={displayMode === 'power' ? headroom.up : headroom.eUp}
              down={displayMode === 'power' ? headroom.down : headroom.eDown}
              formatVal={displayMode === 'power' ? formatPower : formatEnergy}
            />
            {headroom.daPriceEurKwh !== undefined && (
              <div className="mt-3 pt-3 border-t border-border/50 flex items-center gap-6 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <CircleDollarSign className="h-3.5 w-3.5 shrink-0" />
                  <span className="text-muted-foreground/70">DA ref price</span>
                  <span className="font-semibold tabular-nums text-foreground">
                    €{(headroom.daPriceEurKwh * 1000).toFixed(2)}/MWh
                  </span>
                </span>
                {headroom.upDispatchCostEur !== undefined && headroom.upDispatchCostEur > 0 && (
                  <span className="flex items-center gap-1.5">
                    <ArrowUp className="h-3 w-3 text-emerald-500 shrink-0" />
                    <span className="text-muted-foreground/70">Full upward cost</span>
                    <span className="font-semibold tabular-nums text-emerald-600">
                      €{headroom.upDispatchCostEur.toFixed(2)}
                    </span>
                  </span>
                )}
                {headroom.downDispatchCostEur !== undefined && headroom.downDispatchCostEur > 0 && (
                  <span className="flex items-center gap-1.5">
                    <ArrowDown className="h-3 w-3 text-indigo-500 shrink-0" />
                    <span className="text-muted-foreground/70">Full downward saving</span>
                    <span className="font-semibold tabular-nums text-indigo-600">
                      €{headroom.downDispatchCostEur.toFixed(2)}
                    </span>
                  </span>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Band chart — fills remaining height */}
      <div className="flex-1 rounded-xl border bg-card p-5 flex flex-col gap-3 min-h-0 overflow-hidden">
        <div className="flex items-center justify-between shrink-0">
          <div>
            <h3 className="flex items-center gap-1 font-semibold text-sm">
              Next {bandHours} {bandHours === 1 ? 'Hour' : 'Hours'}
              <InfoTip
                content={
                  <>
                    <p className="font-medium mb-1">Upcoming bands — stacked bar preview</p>
                    <p>
                      Each column is one 15-minute interval. Columns are stacked in three segments:
                    </p>
                    <ul className="mt-1 space-y-0.5">
                      <li>
                        <strong className="text-emerald-400">Emerald</strong> — upward flex:
                        pBaseline to pMax.
                      </li>
                      <li>
                        <strong className="text-indigo-400">Indigo</strong> — downward flex: pMin to
                        pBaseline.
                      </li>
                      <li>
                        <strong className="text-muted-foreground">Grey</strong> — floor: 0 to pMin
                        (mandatory minimum).
                      </li>
                    </ul>
                    <p className="mt-1 text-muted-foreground">
                      Click a column to inspect its dispatch headroom above.
                    </p>
                  </>
                }
              />
            </h3>
            <p className="text-xs text-muted-foreground">Upcoming interval power envelope</p>
          </div>
          {/* Hour range selector */}
          <div className="flex items-center gap-1">
            {BAND_HOUR_OPTIONS.map((h) => (
              <Button
                key={h}
                variant={bandHours === h ? 'default' : 'outline'}
                size="sm"
                onClick={() => setBandHours(h)}
                className="h-7 px-2 text-xs"
              >
                {h}h
              </Button>
            ))}
          </div>
        </div>

        {bandsLoading && upcomingBands.length === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : upcomingBands.length === 0 ? (
          <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
            No forecast data
          </div>
        ) : (
          <div className="flex-1 flex flex-col gap-3 min-h-0">
            {/* Stacked band bars */}
            <div className="flex-1 flex items-end gap-0.5 min-h-0 px-1">
              {upcomingBands.map((b: any, i: number) => {
                const eMax = b.eMax ?? 0
                const eBaseline = b.eBaseline ?? 0
                const eMin = b.eMin ?? 0
                const isSelected =
                  selectedBandIndex === i || (selectedBandIndex === null && i === 0)

                const durationMs = new Date(b.endAt).getTime() - new Date(b.startAt).getTime()
                const toPower = (wh: number) => energyToIntervalPower(wh, durationMs)

                const floorPct = (eMin / maxBandVal) * 100
                const baselinePct = (eBaseline / maxBandVal) * 100
                const maxPct = (eMax / maxBandVal) * 100
                const upwardPct = maxPct - baselinePct
                const midPct = baselinePct - floorPct
                const floorSegPct = floorPct

                const price: number | undefined = b.daPriceEurKwh
                const priceLabel =
                  price !== undefined ? `\nDA Price: €${(price * 1000).toFixed(2)}/MWh` : ''

                return (
                  <div
                    key={i}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedBandIndex(isSelected ? null : i)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ')
                        setSelectedBandIndex(isSelected ? null : i)
                    }}
                    className={cn(
                      'flex-1 flex flex-col justify-end h-full gap-px cursor-pointer rounded-sm transition-opacity',
                      isSelected ? 'ring-1 ring-foreground/30' : 'opacity-90 hover:opacity-100'
                    )}
                    title={`${fmt(new Date(b.startAt))}\nMax: ${fmtBand(toPower(eMax), eMax)}\nBaseline: ${fmtBand(toPower(eBaseline), eBaseline)}\nMin: ${fmtBand(toPower(eMin), eMin)}${priceLabel}`}
                  >
                    {upwardPct > 0 && (
                      <div
                        className="w-full rounded-t-sm shrink-0"
                        style={{
                          height: `${upwardPct}%`,
                          background: isSelected
                            ? 'hsl(160,60%,50%,0.65)'
                            : 'hsl(160,60%,50%,0.28)',
                          borderTop: isSelected
                            ? '2px solid hsl(160,60%,50%)'
                            : '1px solid hsl(160,60%,50%,0.5)',
                        }}
                      />
                    )}
                    {midPct > 0 && (
                      <div
                        className="w-full shrink-0"
                        style={{
                          height: `${midPct}%`,
                          background: isSelected
                            ? 'hsl(245,70%,60%,0.55)'
                            : 'hsl(245,70%,60%,0.22)',
                          borderTop:
                            upwardPct === 0
                              ? isSelected
                                ? '2px solid hsl(245,70%,60%)'
                                : '1px solid hsl(245,70%,60%,0.5)'
                              : undefined,
                        }}
                      />
                    )}
                    {floorSegPct > 0 && (
                      <div
                        className="w-full rounded-b-sm shrink-0"
                        style={{
                          height: `${floorSegPct}%`,
                          background: isSelected ? 'hsl(0,0%,50%,0.45)' : 'hsl(0,0%,50%,0.18)',
                        }}
                      />
                    )}
                  </div>
                )
              })}
            </div>

            {/* Time labels */}
            <div className="flex justify-between text-[10px] text-muted-foreground shrink-0 px-1">
              {(() => {
                const count = upcomingBands.length
                if (count === 0) return null
                const tickCount = Math.min(5, count)
                const indices =
                  tickCount <= 1
                    ? [0]
                    : Array.from({ length: tickCount }, (_, k) =>
                        Math.round((k / (tickCount - 1)) * (count - 1))
                      )
                return indices.map((i) =>
                  upcomingBands[i] ? (
                    <span key={i}>
                      {new Date((upcomingBands[i] as any).startAt).toLocaleTimeString('en-US', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  ) : null
                )
              })()}
            </div>

            {/* Legend */}
            <div className="flex items-center gap-3 text-[10px] text-muted-foreground shrink-0">
              <span className="flex items-center gap-1">
                <span className="inline-block h-2 w-3 rounded-sm bg-emerald-500/40 border border-emerald-500/60" />
                Upward flex
              </span>
              <span className="flex items-center gap-1">
                <span className="inline-block h-2 w-3 rounded-sm bg-indigo-500/30 border border-indigo-500/50" />
                Downward flex
              </span>
              <span className="flex items-center gap-1">
                <span className="inline-block h-2 w-3 rounded-sm bg-muted-foreground/25" />
                Floor
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Forecast view ────────────────────────────────────────────────────────────

// FORECAST_CARD_COUNT removed — both charts now display simultaneously

function ForecastView({
  poolID,
  displayMode,
  setDisplayMode,
  chartView,
  onChartViewChange,
  midpointMix,
  onMidpointMixChange,
  normalizeCumulative,
  onNormalizeCumulativeChange,
}: {
  poolID: string
  displayMode: DisplayMode
  setDisplayMode: (mode: DisplayMode) => void
  chartView: 'interval' | 'cumulative' | 'both'
  onChartViewChange: (view: 'interval' | 'cumulative' | 'both') => void
  midpointMix: number
  onMidpointMixChange: (v: number) => void
  normalizeCumulative: boolean
  onNormalizeCumulativeChange: (v: boolean) => void
}) {
  return (
    <div className="h-full flex flex-col px-4 py-4 overflow-hidden">
      <FlexibilityBandChart
        poolID={poolID}
        displayMode={displayMode}
        onDisplayModeChange={setDisplayMode}
        chartView={chartView}
        onChartViewChange={onChartViewChange}
        midpointMix={midpointMix}
        onMidpointMixChange={onMidpointMixChange}
        normalizeCumulative={normalizeCumulative}
        onNormalizeCumulativeChange={onNormalizeCumulativeChange}
      />
    </div>
  )
}

// ─── History view ─────────────────────────────────────────────────────────────

const DAY_HEADERS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']

/** Format a "YYYY-MM-DD" string as a compact label, e.g. "Mar 24, 2026" */
function formatDay(date: string): string {
  const [year, month, day] = date.split('-').map(Number)
  return `${MONTH_NAMES[month - 1]} ${day}, ${year}`
}

interface DayGridPickerProps {
  value: string // "YYYY-MM-DD"
  onChange: (v: string) => void
  disabled?: boolean
}

function DayGridPicker({ value, onChange, disabled }: DayGridPickerProps) {
  const now = new Date()
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`

  const [valueYear, valueMonth] = value.split('-').map(Number)
  const [pickerYear, setPickerYear] = useState(valueYear)
  const [pickerMonth, setPickerMonth] = useState(valueMonth) // 1-based
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // Sync picker year/month when value changes externally
  useEffect(() => {
    const [y, m] = value.split('-').map(Number)
    setPickerYear(y)
    setPickerMonth(m)
  }, [value])

  // Close on outside click
  useEffect(() => {
    if (!open) return
    function handle(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [open])

  function prevMonth() {
    if (pickerMonth === 1) {
      setPickerMonth(12)
      setPickerYear((y) => y - 1)
    } else {
      setPickerMonth((m) => m - 1)
    }
  }

  function nextMonth() {
    const isAtOrAfterNow =
      pickerYear > now.getFullYear() ||
      (pickerYear === now.getFullYear() && pickerMonth >= now.getMonth() + 1)
    if (isAtOrAfterNow) return
    if (pickerMonth === 12) {
      setPickerMonth(1)
      setPickerYear((y) => y + 1)
    } else {
      setPickerMonth((m) => m + 1)
    }
  }

  // Days in the displayed month
  const daysInMonth = new Date(pickerYear, pickerMonth, 0).getDate()
  // Day-of-week offset for the 1st (0=Mon … 6=Sun)
  const firstDow = (new Date(pickerYear, pickerMonth - 1, 1).getDay() + 6) % 7

  const ym = `${pickerYear}-${String(pickerMonth).padStart(2, '0')}`
  const isNextMonthDisabled =
    pickerYear > now.getFullYear() ||
    (pickerYear === now.getFullYear() && pickerMonth >= now.getMonth() + 1)

  return (
    <div ref={containerRef} className="relative">
      <Button
        variant="outline"
        size="sm"
        className="h-8 px-3 text-xs font-normal min-w-[130px] justify-between gap-1"
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
      >
        <span>{formatDay(value)}</span>
        <CalendarDays className="h-3 w-3 opacity-50 shrink-0" />
      </Button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 rounded-md border bg-popover text-popover-foreground shadow-md p-2 w-56">
          {/* Month/year navigation */}
          <div className="flex items-center justify-between mb-2 px-1">
            <button
              className="h-6 w-6 flex items-center justify-center rounded hover:bg-accent"
              onClick={prevMonth}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm font-medium">
              {MONTH_NAMES[pickerMonth - 1]} {pickerYear}
            </span>
            <button
              className={cn(
                'h-6 w-6 flex items-center justify-center rounded hover:bg-accent',
                isNextMonthDisabled && 'opacity-30 pointer-events-none'
              )}
              onClick={nextMonth}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          {/* Day-of-week headers */}
          <div className="grid grid-cols-7 gap-0.5 mb-0.5">
            {DAY_HEADERS.map((h) => (
              <span
                key={h}
                className="text-[10px] text-muted-foreground text-center leading-none py-0.5"
              >
                {h}
              </span>
            ))}
          </div>
          {/* Day grid */}
          <div className="grid grid-cols-7 gap-0.5">
            {/* Leading empty cells */}
            {Array.from({ length: firstDow }).map((_, i) => (
              <span key={`e-${i}`} />
            ))}
            {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => {
              const dateStr = `${ym}-${String(day).padStart(2, '0')}`
              const isFuture = dateStr > todayStr
              const isSelected = dateStr === value
              return (
                <button
                  key={dateStr}
                  disabled={isFuture}
                  onClick={() => {
                    onChange(dateStr)
                    setOpen(false)
                  }}
                  className={cn(
                    'rounded px-0.5 py-1 text-xs transition-colors leading-none text-center',
                    isSelected
                      ? 'bg-primary text-primary-foreground font-medium'
                      : 'hover:bg-accent',
                    isFuture && 'opacity-30 pointer-events-none'
                  )}
                >
                  {day}
                </button>
              )
            })}
          </div>
          {/* Selected date hint */}
          <p className="mt-2 text-[10px] text-muted-foreground text-center leading-tight">
            {formatDay(value)}
          </p>
        </div>
      )}
    </div>
  )
}

type CalendarMode = 'month' | 'week' | 'day'

function HistoryView({
  poolID,
  cardIndex,
  onCardIndexChange,
  timeRange,
  onTimeRangeChange,
  scatterThreshold,
  onScatterThresholdChange,
  scatterEarlyUnplugToleranceMin,
  onScatterEarlyUnplugToleranceMinChange,
  scatterHiddenOutcomes,
  onScatterHiddenOutcomesChange,
  scatterHiddenDurations,
  onScatterHiddenDurationsChange,
  scatterHiddenTimings,
  onScatterHiddenTimingsChange,
  scatterHiddenFeasibilities,
  onScatterHiddenFeasibilitiesChange,
  scatterSelectedSessionId,
  onScatterSelectedSessionIdChange,
}: {
  poolID: string
  cardIndex: number
  onCardIndexChange: (i: number) => void
  timeRange: TimeRange
  onTimeRangeChange: (r: TimeRange) => void
  scatterThreshold: number
  onScatterThresholdChange: (v: number) => void
  scatterEarlyUnplugToleranceMin: number
  onScatterEarlyUnplugToleranceMinChange: (v: number) => void
  scatterHiddenOutcomes: Set<string>
  onScatterHiddenOutcomesChange: (s: Set<string>) => void
  scatterHiddenDurations: Set<string>
  onScatterHiddenDurationsChange: (s: Set<string>) => void
  scatterHiddenTimings: Set<string>
  onScatterHiddenTimingsChange: (s: Set<string>) => void
  scatterHiddenFeasibilities: Set<string>
  onScatterHiddenFeasibilitiesChange: (s: Set<string>) => void
  scatterSelectedSessionId: string | null
  onScatterSelectedSessionIdChange: (id: string | null) => void
}) {
  const now = new Date()
  // Derive a safe default: last completed month
  const defaultMonth =
    now.getMonth() === 0
      ? `${now.getFullYear() - 1}-12`
      : `${now.getFullYear()}-${String(now.getMonth()).padStart(2, '0')}`
  // Derive a safe default: last completed week
  const dayOfWeek = (now.getDay() + 6) % 7
  const thisWeekMonday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - dayOfWeek)
  const lastWeekMonday = new Date(thisWeekMonday.getTime() - 7 * 86400000)
  const defaultWeek = getISOWeek(lastWeekMonday)

  // Derive calendarMode from the current timeRange value
  const calendarMode: CalendarMode =
    typeof timeRange === 'object' && timeRange.type === 'week'
      ? 'week'
      : typeof timeRange === 'object' && timeRange.type === 'day'
        ? 'day'
        : 'month'

  const { isLoading, refetch } = useChargingSessions(poolID, timeRange)

  const CARDS = ['heatmap', 'scatter'] as const
  const CARD_LABELS = ['Session Heatmap', 'SoC Journey']
  const cardCount = CARDS.length

  const isRolling = typeof timeRange === 'string'

  function switchToCalendar(mode: CalendarMode) {
    if (mode === 'month') {
      onTimeRangeChange({ type: 'month', month: defaultMonth })
    } else if (mode === 'week') {
      onTimeRangeChange({ type: 'week', isoWeek: defaultWeek })
    } else {
      // day — default to yesterday (last completed day)
      const yesterday = new Date()
      yesterday.setDate(yesterday.getDate() - 1)
      const y = yesterday.getFullYear()
      const m = String(yesterday.getMonth() + 1).padStart(2, '0')
      const d = String(yesterday.getDate()).padStart(2, '0')
      onTimeRangeChange({ type: 'day', date: `${y}-${m}-${d}` })
    }
  }

  function switchToRolling(window: '24h' | '7d' | '30d') {
    onTimeRangeChange(window)
  }

  /** Step the current calendar period by +1 or -1 unit */
  function stepCalendar(dir: -1 | 1) {
    if (calendarMode === 'day') {
      const date = (timeRange as { type: 'day'; date: string }).date
      const d = new Date(date)
      d.setDate(d.getDate() + dir)
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const dd = String(d.getDate()).padStart(2, '0')
      onTimeRangeChange({ type: 'day', date: `${y}-${m}-${dd}` })
    } else if (calendarMode === 'week') {
      const isoWeek = (timeRange as { type: 'week'; isoWeek: string }).isoWeek
      const monday = isoWeekToMonday(isoWeek)
      monday.setDate(monday.getDate() + dir * 7)
      onTimeRangeChange({ type: 'week', isoWeek: getISOWeek(monday) })
    } else {
      const month = (timeRange as { type: 'month'; month: string }).month
      const [y, m] = month.split('-').map(Number)
      let newY = y
      let newM = m + dir
      if (newM > 12) {
        newM = 1
        newY++
      }
      if (newM < 1) {
        newM = 12
        newY--
      }
      onTimeRangeChange({ type: 'month', month: `${newY}-${String(newM).padStart(2, '0')}` })
    }
  }

  /** Is the current calendar period already the most recent allowed one? */
  function isAtLatestPeriod(): boolean {
    if (calendarMode === 'day') {
      const date = (timeRange as { type: 'day'; date: string }).date
      const now = new Date()
      const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
      return date >= todayStr
    }
    if (calendarMode === 'week') {
      const isoWeek = (timeRange as { type: 'week'; isoWeek: string }).isoWeek
      return isoWeek >= getISOWeek(new Date())
    }
    // month: latest allowed is last completed month
    const month = (timeRange as { type: 'month'; month: string }).month
    const now = new Date()
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
    return month >= currentMonth
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2 px-4 pt-3 pb-1 shrink-0">
        {/* Card title indicator */}
        <span className="text-xs font-medium text-muted-foreground tabular-nums">
          {cardIndex + 1} / {cardCount} — {CARD_LABELS[cardIndex]}
        </span>
        {/* Rolling presets */}
        <div className="flex gap-1">
          {(['24h', '7d', '30d'] as const).map((r) => (
            <Button
              key={r}
              variant={isRolling && timeRange === r ? 'default' : 'outline'}
              size="sm"
              onClick={() => switchToRolling(r)}
              disabled={isLoading}
            >
              {r}
            </Button>
          ))}
        </div>

        {/* Divider */}
        <div className="w-px h-5 bg-border" />

        {/* Calendar mode toggle */}
        <div className="flex gap-1">
          {(['day', 'week', 'month'] as const).map((mode) => (
            <Button
              key={mode}
              variant={!isRolling && calendarMode === mode ? 'default' : 'outline'}
              size="sm"
              onClick={() => switchToCalendar(mode)}
              disabled={isLoading}
            >
              {mode === 'month' ? 'Month' : mode === 'week' ? 'Week' : 'Day'}
            </Button>
          ))}
        </div>

        {/* Period selector with prev/next arrows — shown when not in rolling mode */}
        {!isRolling && (
          <div className="flex items-center gap-1">
            <button
              className="h-7 w-6 flex items-center justify-center rounded border hover:bg-accent transition-colors disabled:opacity-30"
              onClick={() => stepCalendar(-1)}
              disabled={isLoading}
              aria-label="Previous period"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </button>
            {calendarMode !== 'day' ? (
              <PeriodGridPicker
                mode={calendarMode}
                value={
                  calendarMode === 'month'
                    ? (timeRange as { type: 'month'; month: string }).month
                    : (timeRange as { type: 'week'; isoWeek: string }).isoWeek
                }
                onChange={(v) => {
                  if (calendarMode === 'month') {
                    onTimeRangeChange({ type: 'month', month: v })
                  } else {
                    onTimeRangeChange({ type: 'week', isoWeek: v })
                  }
                }}
                disabled={isLoading}
              />
            ) : (
              <DayGridPicker
                value={(timeRange as { type: 'day'; date: string }).date}
                onChange={(v) => onTimeRangeChange({ type: 'day', date: v })}
                disabled={isLoading}
              />
            )}
            <button
              className="h-7 w-6 flex items-center justify-center rounded border hover:bg-accent transition-colors disabled:opacity-30"
              onClick={() => stepCalendar(1)}
              disabled={isLoading || isAtLatestPeriod()}
              aria-label="Next period"
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isLoading}>
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
        </Button>
      </div>
      {/* Cards */}
      <div className="flex-1 flex items-stretch gap-2 px-4 pb-4 min-h-0 overflow-hidden">
        {/* Prev button */}
        <button
          onClick={() => onCardIndexChange((cardIndex - 1 + cardCount) % cardCount)}
          className="shrink-0 self-center rounded-md border p-1.5 hover:bg-muted transition-colors disabled:opacity-30"
          disabled={cardCount <= 1}
          aria-label="Previous card"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        {/* Active card */}
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {cardIndex === 0 && <SessionActivityHeatmap poolID={poolID} timeRange={timeRange} />}
          {cardIndex === 1 && (
            <SoCJourneyScatter
              poolID={poolID}
              timeRange={timeRange}
              threshold={scatterThreshold}
              onThresholdChange={onScatterThresholdChange}
              earlyUnplugToleranceMin={scatterEarlyUnplugToleranceMin}
              onEarlyUnplugToleranceMinChange={onScatterEarlyUnplugToleranceMinChange}
              hiddenOutcomes={scatterHiddenOutcomes}
              onHiddenOutcomesChange={onScatterHiddenOutcomesChange}
              hiddenDurations={scatterHiddenDurations}
              onHiddenDurationsChange={onScatterHiddenDurationsChange}
              hiddenTimings={scatterHiddenTimings}
              onHiddenTimingsChange={onScatterHiddenTimingsChange}
              hiddenFeasibilities={scatterHiddenFeasibilities}
              onHiddenFeasibilitiesChange={onScatterHiddenFeasibilitiesChange}
              selectedSessionId={scatterSelectedSessionId}
              onSelectedSessionIdChange={onScatterSelectedSessionIdChange}
            />
          )}
        </div>

        {/* Next button */}
        <button
          onClick={() => onCardIndexChange((cardIndex + 1) % cardCount)}
          className="shrink-0 self-center rounded-md border p-1.5 hover:bg-muted transition-colors disabled:opacity-30"
          disabled={cardCount <= 1}
          aria-label="Next card"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}

// ─── GCP Forecasts view ───────────────────────────────────────────────────────

/** Round a Date down to the nearest 15-minute boundary */
function floorTo15Min(d: Date): Date {
  const ms = d.getTime()
  return new Date(ms - (ms % (15 * 60 * 1000)))
}

const GCP_PURPOSE_COLOR: Record<string, string> = {
  gcp_supply: '#10b981',   // emerald-500
  gcp_feed_in: '#f59e0b',  // amber-500
  gcp_without_ev: '#8b5cf6', // violet-500
}

const NOW_LINE_COLOR = '#94a3b8' // slate-400
const INTERVAL_MS = 15 * 60 * 1000

interface GcpForecastsViewProps {
  poolID: string
  configs: Array<{ purpose: string; isActive: boolean }>
}

function GcpForecastsView({ poolID, configs }: GcpForecastsViewProps) {
  const activeConfigs = configs.filter((c) => c.isActive)
  const { data: forecastData, isLoading, isError } = usePoolGCPForecasts(poolID)
  const nowLineMs = floorTo15Min(new Date()).getTime()

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm">Loading forecasts…</span>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-2 text-muted-foreground">
        <AlertTriangle className="h-6 w-6 text-amber-500" />
        <p className="text-sm">Failed to load GCP forecast data.</p>
      </div>
    )
  }

  if (activeConfigs.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-2 text-muted-foreground">
        <BarChart2 className="h-8 w-8 opacity-30" />
        <p className="text-sm">No active GCP forecast configurations.</p>
      </div>
    )
  }

  const purposeToKey: Record<string, 'gcpSupply' | 'gcpFeedIn' | 'gcpWithoutEv'> = {
    gcp_supply: 'gcpSupply',
    gcp_feed_in: 'gcpFeedIn',
    gcp_without_ev: 'gcpWithoutEv',
  }

  return (
    <div className="h-full overflow-y-auto">
      <div
        className="grid gap-0 p-0"
        style={{ gridTemplateRows: `repeat(${activeConfigs.length}, minmax(0, 1fr))`, height: '100%' }}
      >
        {activeConfigs.map((cfg) => {
          const dataKey = purposeToKey[cfg.purpose]
          const forecastEntry = dataKey ? (forecastData?.[dataKey] ?? null) : null
          const color = GCP_PURPOSE_COLOR[cfg.purpose] ?? '#64748b'

          const chartData: D3FlexChartPoint[] = (forecastEntry?.periods ?? []).map((p) => {
            const ts = new Date(p.timestamp).getTime()
            return { ts, endTs: ts + INTERVAL_MS, forecast: p.value }
          })

          const series: D3FlexChartSeries[] = [
            { key: 'forecast', name: 'Forecast', color, type: 'area', fillOpacity: 0.15, interpolation: 'step-after' },
          ]

          // xDomain = the forecast's own validity window (startAt → endAt).
          // Fall back to a ±48h window around now if metadata is missing.
          const domainStart = forecastEntry?.startAt
            ? new Date(forecastEntry.startAt).getTime()
            : nowLineMs - 48 * INTERVAL_MS * 4 // 48h back
          const domainEnd = forecastEntry?.endAt
            ? new Date(forecastEntry.endAt).getTime()
            : nowLineMs + 48 * INTERVAL_MS * 4 // 48h forward

          const hasForecast = chartData.length > 0

          const purposeLabel = cfg.purpose
            .split('_')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' ')

          return (
            <div key={cfg.purpose} className="flex flex-col border-b last:border-b-0 overflow-hidden">
              {/* Panel header */}
              <div className="flex items-center justify-between px-4 pt-3 pb-1 shrink-0">
                <div className="flex items-center gap-2">
                  <span className="inline-block h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
                  <span className="text-xs font-semibold text-foreground">{purposeLabel}</span>
                </div>
                <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
                  {hasForecast && (
                    <span>
                      {new Date(domainStart).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      {' – '}
                      {new Date(domainEnd).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <span className="inline-block w-3 border-t-2 border-dashed" style={{ borderColor: NOW_LINE_COLOR }} />
                    <span>Now</span>
                  </span>
                </div>
              </div>

              {/* Chart */}
              <div className="flex-1 min-h-0 relative">
                {!hasForecast ? (
                  <div className="h-full flex items-center justify-center text-xs text-muted-foreground">
                    No forecast data available
                  </div>
                ) : (
                  <D3FlexChart
                    data={chartData}
                    series={series}
                    unit="kW"
                    xDomain={[domainStart, domainEnd]}
                    fillContainer
                    snapRevision={cfg.purpose}
                  />
                )}
                {hasForecast && (
                  <NowLineOverlay nowMs={nowLineMs} domainStart={domainStart} domainEnd={domainEnd} color={NOW_LINE_COLOR} />
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/**
 * Dashed vertical "now" line placed at the exact pixel position of nowMs
 * within the D3FlexChart plot area. Mirrors D3FlexChart's MARGIN constants.
 */
function NowLineOverlay({
  nowMs, domainStart, domainEnd, color,
}: { nowMs: number; domainStart: number; domainEnd: number; color: string }) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState(0)

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const obs = new ResizeObserver((entries) => setContainerWidth(entries[0].contentRect.width))
    obs.observe(el)
    setContainerWidth(el.clientWidth)
    return () => obs.disconnect()
  }, [])

  // Must stay in sync with MARGIN in D3FlexChart.tsx
  const marginLeft = 60
  const marginRight = 16
  const plotWidth = containerWidth - marginLeft - marginRight
  const fraction = (nowMs - domainStart) / (domainEnd - domainStart)
  const xPx = marginLeft + fraction * plotWidth

  if (containerWidth === 0 || fraction < 0 || fraction > 1) {
    return <div ref={containerRef} className="absolute inset-0 pointer-events-none" />
  }

  return (
    <div ref={containerRef} className="absolute inset-0 pointer-events-none">
      <div
        className="absolute top-0 bottom-0"
        style={{ left: `${xPx}px`, width: '1px', borderLeft: `2px dashed ${color}`, opacity: 0.7 }}
      />
    </div>
  )
}

// ─── Main dashboard ───────────────────────────────────────────────────────────

const NAV_ITEMS: { view: View; icon: React.ReactNode; label: string; sublabel: string }[] = [
  {
    view: 'overview',
    icon: <Activity className="h-4 w-4" />,
    label: 'Overview',
    sublabel: 'Live pool status at a glance',
  },
  {
    view: 'dispatch',
    icon: <Zap className="h-4 w-4" />,
    label: 'Dispatch',
    sublabel: 'Headroom & upcoming intervals',
  },
  {
    view: 'forecast',
    icon: <TrendingUp className="h-4 w-4" />,
    label: 'Flexibility Band',
    sublabel: '48h forecast view',
  },
  {
    view: 'history',
    icon: <CalendarDays className="h-4 w-4" />,
    label: 'History',
    sublabel: 'Session patterns',
  },
]

export default function MonitoringDashboard({
  poolID,
  poolName,
  poolPurpose,
  forecastConfigs,
}: MonitoringDashboardProps) {
  const params = useMonitoringParams()
  const {
    view,
    setView,
    displayMode,
    setDisplayMode,
    historyCardIndex,
    setHistoryCardIndex,
    historyTimeRange,
    setHistoryTimeRange,
    flexibilityChartView,
    setFlexibilityChartView,
    forecastMidpointMix,
    setForecastMidpointMix,
    forecastNormalizeCumulative,
    setForecastNormalizeCumulative,
    scatterThreshold,
    setScatterThreshold,
    scatterEarlyUnplugToleranceMin,
    setScatterEarlyUnplugToleranceMin,
    scatterHiddenOutcomes,
    setScatterHiddenOutcomes,
    scatterHiddenDurations,
    setScatterHiddenDurations,
    scatterHiddenTimings,
    setScatterHiddenTimings,
    scatterHiddenFeasibilities,
    setScatterHiddenFeasibilities,
    scatterSelectedSessionId,
    setScatterSelectedSessionId,
  } = params
  const isEV = poolPurpose === 'intraday' || poolPurpose === 'intraday_cloud'
  const isBattery = poolPurpose === 'imbalance' || poolPurpose === 'tou_stacking_poc'

  // GCP forecast configs are those whose purpose starts with 'gcp'
  const gcpForecastConfigs = (forecastConfigs ?? []).filter((f) => f.purpose.startsWith('gcp'))
  const hasGcpConfigs = gcpForecastConfigs.length > 0
  const hasActiveGcpConfig = gcpForecastConfigs.some((f) => f.isActive)

  return (
    <div className="flex h-full overflow-hidden rounded-xl border bg-muted/20">
      {/* Left nav rail */}
      <div className="w-44 shrink-0 border-r bg-card flex flex-col py-3 px-2 gap-1">
        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider px-3 pb-1">
          Monitoring
        </p>

        {NAV_ITEMS.filter(
          (item) =>
            (isEV || (item.view !== 'forecast' && item.view !== 'history')) &&
            (!isBattery || item.view !== 'dispatch')
        ).map((item) => (
          <NavItem
            key={item.view}
            icon={item.icon}
            label={item.label}
            sublabel={item.sublabel}
            active={view === item.view}
            onClick={() => setView(item.view)}
          />
        ))}

        {/* GCP Forecasts — visible only when pool has at least one GCP forecast config */}
        {hasGcpConfigs && (
          <NavItem
            icon={<BarChart2 className="h-4 w-4" />}
            label="GCP Forecasts"
            sublabel="Time series per GCP forecast configuration"
            active={view === 'gcp_forecasts'}
            disabled={!hasActiveGcpConfig}
            onClick={() => setView('gcp_forecasts')}
          />
        )}

        {/* Pool info footer */}
        <div className="mt-auto pt-3 border-t mx-1">
          <p className="text-[10px] text-muted-foreground truncate px-1">{poolName}</p>
          {poolPurpose && (
            <p className="text-[10px] text-muted-foreground/60 truncate px-1 capitalize">
              {poolPurpose.replace(/_/g, ' ')}
            </p>
          )}
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 min-w-0 overflow-hidden bg-background">
        {view === 'overview' && isBattery && <BatteryOverviewView poolID={poolID} />}
        {view === 'overview' && !isBattery && (
          <OverviewView
            poolID={poolID}
            poolPurpose={poolPurpose}
            displayMode={displayMode}
            onNavigate={setView}
          />
        )}
        {view === 'dispatch' && (
          <DispatchView
            poolID={poolID}
            poolPurpose={poolPurpose}
            displayMode={displayMode}
            setDisplayMode={setDisplayMode}
          />
        )}
        {view === 'forecast' && isEV && (
          <ForecastView
            poolID={poolID}
            displayMode={displayMode}
            setDisplayMode={setDisplayMode}
            chartView={flexibilityChartView}
            onChartViewChange={setFlexibilityChartView}
            midpointMix={forecastMidpointMix}
            onMidpointMixChange={setForecastMidpointMix}
            normalizeCumulative={forecastNormalizeCumulative}
            onNormalizeCumulativeChange={setForecastNormalizeCumulative}
          />
        )}
        {view === 'history' && isEV && (
          <HistoryView
            poolID={poolID}
            cardIndex={historyCardIndex}
            onCardIndexChange={setHistoryCardIndex}
            timeRange={historyTimeRange}
            onTimeRangeChange={setHistoryTimeRange}
            scatterThreshold={scatterThreshold}
            onScatterThresholdChange={setScatterThreshold}
            scatterEarlyUnplugToleranceMin={scatterEarlyUnplugToleranceMin}
            onScatterEarlyUnplugToleranceMinChange={setScatterEarlyUnplugToleranceMin}
            scatterHiddenOutcomes={scatterHiddenOutcomes}
            onScatterHiddenOutcomesChange={setScatterHiddenOutcomes}
            scatterHiddenDurations={scatterHiddenDurations}
            onScatterHiddenDurationsChange={setScatterHiddenDurations}
            scatterHiddenTimings={scatterHiddenTimings}
            onScatterHiddenTimingsChange={setScatterHiddenTimings}
            scatterHiddenFeasibilities={scatterHiddenFeasibilities}
            onScatterHiddenFeasibilitiesChange={setScatterHiddenFeasibilities}
            scatterSelectedSessionId={scatterSelectedSessionId}
            onScatterSelectedSessionIdChange={setScatterSelectedSessionId}
          />
        )}
        {view === 'gcp_forecasts' && (
          <GcpForecastsView poolID={poolID} configs={gcpForecastConfigs} />
        )}
        {(view === 'forecast' || view === 'history') && !isEV && (
          <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
            Not available for this pool type
          </div>
        )}
      </div>
    </div>
  )
}
